/**
 * The lesson's card and marks (decision 0045). The card says one thing a step: over the world in
 * the start sheet's place on a phone, above the bottom bar so the banner button stays in reach;
 * on a desktop just under what the step shows, following it as the camera moves (Daniel,
 * 2026-11-03). The marks are drawn over the world, in the interface layer: a ring in ink round the
 * tile a step points at, a frame in ink round the nine tiles of the number it explains, and a
 * square in the accent round that number, so that the 1 or the 2 the card speaks of is the one in
 * red among the many on the screen (Daniel, 2026-11-03), all laid on the ground as the camera sees
 * it. The ring and the square blink.
 */
import "./lesson.css";
import type { Coord, LessonStep } from "@mistsweeper/core";
import type { StringKey } from "../i18n.ts";
import { esc, fragment, SQUARE } from "./dom.ts";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

/** What each step's card says: its heading, its text by touch and by mouse, and a second line. */
interface Words {
  readonly title: StringKey;
  readonly touch: readonly StringKey[];
  readonly mouse: readonly StringKey[];
  readonly more?: StringKey;
}

const WORDS: readonly Words[] = [
  { title: "lesson.cabinTitle", touch: ["lesson.cabinText"], mouse: ["lesson.cabinTextMouse"] },
  {
    title: "lesson.numberTitle",
    touch: ["lesson.numberText", "lesson.numberHold"],
    mouse: ["lesson.numberText", "lesson.numberMouse"],
    more: "lesson.numberMode",
  },
  { title: "lesson.safeTitle", touch: ["lesson.safeText"], mouse: ["lesson.safeText"] },
  { title: "lesson.ownTitle", touch: ["lesson.ownText"], mouse: ["lesson.ownText"] },
  {
    title: "lesson.endTitle",
    touch: ["lesson.endText", "lesson.endDistance"],
    mouse: ["lesson.endText", "lesson.endDistance"],
  },
];

/** The ring's radius round its tile's centre, the frame's half side and the number's, in tiles. */
const RING = 0.62;
const FRAME = 1.5;
const NUMBER = 0.44;
/** On a desktop, the card's distance under what the step shows, and from the window's edges. */
export const GAP = 20;
const EDGE = 24;
/** Points along the ring: enough for a circle laid on the ground to look round. */
const RING_POINTS = 40;

const SVG = "http://www.w3.org/2000/svg";

export interface LessonCardHandlers {
  skip(): void;
  /** The last card's button. */
  finish(): void;
}

/**
 * How the lesson was opened: by itself on a first visit, by itself before joining friends from
 * an invitation, or from How to play. It decides the first card's lead and the last button.
 */
export type LessonWay = "first" | "invite" | "menu";

const FINISH: Readonly<Record<LessonWay, StringKey>> = {
  first: "start.setOut",
  invite: "welcome.next",
  menu: "lesson.done",
};

export class LessonCard {
  private readonly card: HTMLElement;
  private readonly marks: SVGSVGElement;
  private readonly ring: SVGPolygonElement[];
  private readonly frame: SVGPolygonElement[];
  private readonly number: SVGPolygonElement[];
  private readonly t: T;
  private readonly desk: () => boolean;
  /** How far from the cabin the lesson's ground reaches, written out, for the last card. */
  private readonly distance: () => string;
  private shown: LessonStep | null = null;
  private index = 0;

  private readonly way: LessonWay;

  constructor(
    layer: HTMLElement,
    t: T,
    way: LessonWay,
    desk: () => boolean,
    distance: () => string,
    handlers: LessonCardHandlers,
  ) {
    this.t = t;
    this.way = way;
    this.desk = desk;
    this.distance = distance;
    this.marks = document.createElementNS(SVG, "svg");
    this.marks.classList.add("lesson-marks");
    this.marks.setAttribute("aria-hidden", "true");
    const pair = (cls: string) =>
      ["halo", "line"].map((part) => {
        const p = document.createElementNS(SVG, "polygon");
        p.classList.add(cls, part);
        this.marks.append(p);
        return p;
      });
    this.frame = pair("lesson-frame");
    this.ring = pair("lesson-ring");
    this.number = pair("lesson-number");
    this.card = fragment(`
      <section class="lesson-card" aria-labelledby="lessonTitle">
        <div class="lesson-top">
          <div class="lesson-steps" role="img"></div>
          <button type="button" class="link lesson-skip" data-lesson="skip">${esc(t("welcome.skip"))}</button>
        </div>
        <div class="lesson-words" aria-live="polite">
          <p class="lesson-lead"></p>
          <div class="headline">${SQUARE}<h2 id="lessonTitle"></h2></div>
          <p class="lesson-text"></p>
          <p class="lesson-more"></p>
        </div>
        <button type="button" class="primary lesson-go" data-lesson="finish">${esc(t(FINISH[way]))}</button>
      </section>`);
    this.card.addEventListener("click", (e) => {
      const target = (e.target as Element).closest<HTMLElement>("[data-lesson]");
      if (target?.dataset.lesson === "skip") handlers.skip();
      if (target?.dataset.lesson === "finish") handlers.finish();
    });
    layer.prepend(this.marks);
    layer.append(this.card);
    layer.classList.add("in-lesson");
  }

  /** The card of a step, and its marks from the next frame on. */
  show(index: number, step: LessonStep): void {
    this.index = index;
    this.shown = step;
    const words = WORDS[index] as Words;
    const total = WORDS.length;
    const squares = this.card.querySelector(".lesson-steps") as HTMLElement;
    squares.setAttribute("aria-label", this.t("welcome.step", { n: index + 1, total }));
    squares.innerHTML = WORDS.map((_, i) => `<span${i <= index ? ' class="on"' : ""}></span>`).join(
      "",
    );
    (this.card.querySelector("h2") as HTMLElement).textContent = this.t(words.title);
    // Friends wait for someone who came by their invitation: said once, on the first card.
    const lead = this.card.querySelector(".lesson-lead") as HTMLElement;
    lead.textContent = this.way === "invite" && index === 0 ? this.t("lesson.invite") : "";
    lead.hidden = lead.textContent === "";
    this.refreshWords();
    const last = index === total - 1;
    (this.card.querySelector(".lesson-skip") as HTMLElement).hidden = last;
    (this.card.querySelector(".lesson-go") as HTMLElement).hidden = !last;
  }

  /** The words by the way the player plays: touch or mouse, which may change as they go. */
  refreshWords(): void {
    const words = WORDS[this.index] as Words;
    const desk = this.desk();
    const params = { distance: this.distance() };
    const text = (desk ? words.mouse : words.touch).map((key) => this.t(key, params)).join(" ");
    (this.card.querySelector(".lesson-text") as HTMLElement).textContent = text;
    const more = this.card.querySelector(".lesson-more") as HTMLElement;
    more.textContent = !desk && words.more ? this.t(words.more) : "";
    more.hidden = desk || !words.more;
  }

  /**
   * Lays the marks on the ground where their tiles are now, and on a desktop stands the card just
   * under `shown`, the tiles the step shows. `project` gives a point of the ground, in tiles, in
   * CSS pixels.
   */
  place(
    project: (x: number, y: number) => { x: number; y: number },
    shown: readonly Coord[],
  ): void {
    const step = this.shown;
    const points = (list: readonly { x: number; y: number }[]) =>
      list.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const ring = step?.ring ? circle(step.ring).map((p) => project(p.x, p.y)) : [];
    const frame = step?.frame ? square(step.frame, FRAME).map((p) => project(p.x, p.y)) : [];
    const number = step?.frame ? square(step.frame, NUMBER).map((p) => project(p.x, p.y)) : [];
    for (const p of this.ring) p.setAttribute("points", points(ring));
    for (const p of this.frame) p.setAttribute("points", points(frame));
    for (const p of this.number) p.setAttribute("points", points(number));
    if (!this.desk()) {
      this.card.style.transform = "";
      return;
    }
    const corners = [
      ...ring,
      ...frame,
      ...shown.flatMap((t) => square(t, 0.5)).map((p) => project(p.x, p.y)),
    ];
    if (corners.length === 0) return;
    const left = Math.min(...corners.map((p) => p.x));
    const right = Math.max(...corners.map((p) => p.x));
    const bottom = Math.max(...corners.map((p) => p.y));
    const w = this.card.offsetWidth;
    const h = this.card.offsetHeight;
    const x = clamp((left + right) / 2 - w / 2, EDGE, innerWidth - w - EDGE);
    const y = clamp(bottom + GAP, EDGE, innerHeight - h - EDGE);
    this.card.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
  }

  /** The card's height in CSS pixels, which the camera leaves room for. */
  get height(): number {
    return this.card.offsetHeight;
  }

  /** The lesson is left: the card and the marks go. */
  remove(): void {
    this.card.parentElement?.classList.remove("in-lesson");
    this.card.remove();
    this.marks.remove();
  }
}

function circle(c: Coord): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < RING_POINTS; i += 1) {
    const a = (i / RING_POINTS) * Math.PI * 2;
    out.push({ x: c.x + RING * Math.cos(a), y: c.y + RING * Math.sin(a) });
  }
  return out;
}

function square(c: Coord, half: number): { x: number; y: number }[] {
  return [
    { x: c.x - half, y: c.y - half },
    { x: c.x + half, y: c.y - half },
    { x: c.x + half, y: c.y + half },
    { x: c.x - half, y: c.y + half },
  ];
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(Math.max(lo, hi), v));
}
